/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */

import moment from "moment-timezone";
import { startCase } from "lodash";

import { ActionKind, ColumnKind, HydratedFilterAction, Schema } from "../shared";
import { enumValueToEnumOption, formattedValue, getColumnDefByJsonKey } from "../helpers";
import { DisplayValue, Operator, tagText, operatorToDateValue } from "../operators";
import { handlerMap } from "./handlers";

const { IsTrue, Between } = Operator;

const getDateTimeDisplayText = (timestamp, name, operator, isTime = false): string => {
  const formatString = isTime ? "MM/DD/YYYY h:mm" : "MM/DD/YYYY";
  const formattedDate = moment(timestamp)
    .tz(moment.tz.guess())
    .format(formatString);

  if (operator === Between) {
    const [formattedStart, formattedEnd] = timestamp.map(t =>
      moment(t)
        .tz(moment.tz.guess())
        .format(formatString),
    );

    operatorToDateValue[Between] = `${formattedStart} and ${formattedEnd}`;
  }

  return tagText(name, operator, operatorToDateValue[operator] || formattedDate);
};
/*
 * getFilterAction will return an ActionDef object that can be added to the actions stack
 * It contains the logic for handling the correct ActionDef based on the ColumnKind and Operator kind
 */
const getFilterAction = (
  s: Schema,
  jsonKey: string,
  operator: Operator,
  comparisonValue?: any, // hush typescript
): HydratedFilterAction => {
  const def = getColumnDefByJsonKey(s, jsonKey);
  const { kind, displayName } = def;
  const name = startCase(displayName);

  /*
   * ColumnKind-dependent function implementations
   * For example, checking equality is done differently for numbers vs dates
   */
  if (operator === Operator.IsNotEmpty || operator === Operator.IsEmpty) {
    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      displayText: tagText(
        name,
        operator,
        operator === Operator.IsEmpty ? DisplayValue.Empty : DisplayValue.NotEmpty,
      ),
      func: handlerMap(kind, operator, jsonKey),
    };
  }
  if (kind === ColumnKind.Boolean) {
    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      displayText: tagText(
        name,
        operator,
        operator === IsTrue ? DisplayValue.True : DisplayValue.False,
      ),
      func: handlerMap(kind, operator, jsonKey),
    };
  }

  if (kind === ColumnKind.Date) {
    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      comparisonValue,
      displayText: getDateTimeDisplayText(comparisonValue, name, operator),
      func: handlerMap(kind, operator, jsonKey, comparisonValue),
    };
  }
  if (kind === ColumnKind.Enum) {
    const enumValuesToEnumOptions = comparisonValue.map(enumValue =>
      enumValueToEnumOption(def.enums, enumValue),
    );

    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      comparisonValue,
      displayText: tagText(
        name,
        operator,
        enumValuesToEnumOptions.map(cv => cv.label).join(" OR "),
      ),
      func: handlerMap(kind, operator, jsonKey, comparisonValue),
    };
  }
  if (kind === ColumnKind.Money) {
    const num = parseFloat(comparisonValue);
    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      comparisonValue,
      displayText: tagText(name, operator, formattedValue(ColumnKind.Money, num)),
      func: handlerMap(kind, operator, jsonKey, num),
    };
  }
  if (kind === ColumnKind.Number) {
    const num = parseInt(comparisonValue, 10);
    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      comparisonValue,
      displayText: tagText(name, operator, formattedValue(ColumnKind.Number, num)),
      func: handlerMap(kind, operator, jsonKey, num),
    };
  }
  if (kind === ColumnKind.Text) {
    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      comparisonValue,
      displayText: tagText(name, operator, comparisonValue),
      func: handlerMap(kind, operator, jsonKey, comparisonValue),
    };
  }
  if (kind === ColumnKind.Time) {
    return {
      kind: ActionKind.Filter,
      jsonKey,
      operator,
      comparisonValue,
      displayText: getDateTimeDisplayText(comparisonValue, name, operator, true),
      func: handlerMap(kind, operator, jsonKey, comparisonValue),
    };
  }

  // Invalid Column Kind found, throw error to fail loudly
  throw new Error(`Column kind "${def.kind}" is invalid`);
};

export default getFilterAction;
